import { useCallback, useEffect, useRef, useState } from 'react';
import socket from '../socket/socket';
import { invalidateAdminAlerts, loadAdminAlerts } from '../services/adminAlertsApi';
import { API_BASE } from './config';
import { apiJson } from '../utils/apiFetch';

const clamp = (x, y, width, height) => ({
  x: Math.max(8, Math.min(window.innerWidth - width - 8, x)),
  y: Math.max(8, Math.min(window.innerHeight - height - 8, y))
});

const positionPanelBeside = (button, preferredWidth = 420, preferredHeight = 560) => {
  const rect = button.getBoundingClientRect();
  const width = Math.min(preferredWidth, window.innerWidth - 16);
  const height = Math.min(preferredHeight, window.innerWidth <= 700 ? window.innerHeight * 0.42 : window.innerHeight - 16);
  if (window.innerWidth <= 700) {
    const below = rect.bottom + 8;
    return clamp(8, below + height <= window.innerHeight - 8 ? below : rect.top - height - 8, width, height);
  }
  if (rect.right + 10 + width <= window.innerWidth - 8) return clamp(rect.right + 10, rect.top, width, height);
  if (rect.left - 10 - width >= 8) return clamp(rect.left - width - 10, rect.top, width, height);
  return clamp(rect.left, rect.bottom + 8, width, height);
};

function AdminMatchRequestInbox({ groupId }) {
  const [requests, setRequests] = useState([]);
  const [open, setOpen] = useState(false);
  const [buttonPosition, setButtonPosition] = useState(null);
  const [panelPosition, setPanelPosition] = useState(null);
  const [requestBusy, setRequestBusy] = useState(null);
  const [actionError, setActionError] = useState('');
  const dragRef = useRef(null);
  const movedRef = useRef(false);
  const pendingCount = requests.filter(item => item.status === 'OPEN').length;

  const load = useCallback(async () => {
    if (!groupId) return;
    try {
      const result = await loadAdminAlerts(groupId);
      setRequests(result.matchRequests || []);
    } catch (error) { console.error('LOAD MATCH REQUEST INBOX ERROR:', error); }
  }, [groupId]);

  useEffect(() => {
    load();
    const refresh = payload => {
      if ((!payload?.groupId || Number(payload.groupId) === Number(groupId)) && String(payload?.type || '').startsWith('MATCH_REQUEST')) { invalidateAdminAlerts(groupId); load(); if (payload?.type === 'MATCH_REQUEST') setOpen(true); }
    };
    const refreshMatchState = payload => {
      if (!payload?.groupId || Number(payload.groupId) === Number(groupId)) {
        invalidateAdminAlerts(groupId);
        load();
      }
    };
    socket.on('admin-alert-updated', refresh);
    socket.on('queue-updated', refreshMatchState);
    socket.on('match-updated', refreshMatchState);
    const timer = window.setInterval(load, 30000);
    return () => { socket.off('admin-alert-updated', refresh); socket.off('queue-updated', refreshMatchState); socket.off('match-updated', refreshMatchState); window.clearInterval(timer); };
  }, [groupId, load]);

  const start = (event, kind) => {
    if (kind === 'panel' && (event.target.closest('button') || !event.target.closest('header'))) return;
    const rect = event.currentTarget.getBoundingClientRect();
    movedRef.current = false;
    dragRef.current = { kind, x: event.clientX, y: event.clientY, left: rect.left, top: rect.top, width: rect.width, height: rect.height };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };
  const move = event => {
    const drag = dragRef.current;
    if (!drag) return;
    const dx = event.clientX - drag.x;
    const dy = event.clientY - drag.y;
    if (Math.abs(dx) + Math.abs(dy) > 5) movedRef.current = true;
    const next = clamp(drag.left + dx, drag.top + dy, drag.width, drag.height);
    drag.kind === 'button' ? setButtonPosition(next) : setPanelPosition(next);
  };
  const stop = event => {
    if (!dragRef.current) return;
    dragRef.current = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  };

  const approveRequest = async request => {
    if (!groupId || requestBusy) return;
    setRequestBusy(request.id);
    setActionError('');
    try {
      await apiJson(`${API_BASE}/api/match-planner/${Number(groupId)}/requests/${Number(request.id)}/decision`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token') || ''}`
        },
        body: JSON.stringify({ decision: 'APPROVE' })
      });
      invalidateAdminAlerts(groupId);
      await load();
    } catch (error) {
      setActionError(error.message || 'ไม่สามารถ Approve คำขอนี้ได้');
    } finally {
      setRequestBusy(null);
    }
  };

  return <>
    <button type="button" className={`admin-message-float is-match-request${requests.length === 0 ? ' is-empty' : ''}`} style={buttonPosition ? { left: buttonPosition.x, top: buttonPosition.y, right: 'auto' } : undefined} onPointerDown={event => start(event, 'button')} onPointerMove={move} onPointerUp={stop} onPointerCancel={stop} onClick={event => { if (movedRef.current) { movedRef.current = false; return; } setPanelPosition(positionPanelBeside(event.currentTarget)); setOpen(value => !value); }} title="กดเพื่อเปิด • ลากเพื่อย้ายตำแหน่ง">
      <i aria-hidden="true">🏸</i><span>คำขอจัดคู่</span>{pendingCount > 0 && <b>{pendingCount}</b>}
    </button>
    {open && <aside className="admin-message-panel is-match-request" style={panelPosition ? { left: panelPosition.x, top: panelPosition.y, right: 'auto' } : undefined} onPointerDown={event => start(event, 'panel')} onPointerMove={move} onPointerUp={stop} onPointerCancel={stop}>
      <header><div><strong>🏸 คำขอจัดคู่จากผู้เล่น</strong><small>รอดำเนินการ {pendingCount} • ลากหัวกล่องเพื่อย้าย</small></div><button type="button" onClick={() => setOpen(false)}>×</button></header>
      <section>
        {actionError && <p className="admin-match-request-error" role="alert">{actionError}</p>}
        {requests.length === 0 && <p>ยังไม่มีคำขอจัดคู่ใหม่</p>}
        {requests.map(item => <article key={item.id} className={item.fulfilled ? 'is-fulfilled' : item.status === 'APPROVED' ? 'is-approved' : ''}>
          <div><strong>{item.requester_name}</strong><span>{(item.players || []).map(player => player.name).join(' + ') || `ขอจัดคู่/จัด Match · ${Number(item.player_count || 0)} คน`}</span>{item.status === 'APPROVED' && !item.fulfilled && <small>อนุมัติแล้ว • รอจัดลง Match</small>}{item.fulfilled && <small>✓ จัดลงตามคำขอแล้ว{item.matched_group_match_no ? ` · Match #${item.matched_group_match_no}` : ''}</small>}</div>
          {item.status === 'OPEN' ? <button type="button" className="admin-match-request-approve" disabled={requestBusy !== null} onClick={() => approveRequest(item)}>
            {requestBusy === item.id ? 'กำลังอนุมัติ…' : '✓ Approve'}
          </button> : <b className="admin-match-request-status">{item.fulfilled ? '✓' : '…'}</b>}
        </article>)}
      </section>
    </aside>}
  </>;
}

export default AdminMatchRequestInbox;
